/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Exocet Product Motion — PDP hero + section reveals
 */
(function () {
  "use strict";

  if (document.documentElement.dataset.pdpMotion !== "on") return;

  var BOUND = "data-pdp-motion-bound";
  var VISIBLE = "pdp-motion-visible";

  var SECTION_TARGETS = [
    {
      root: ".design-dna-2026[data-pdp-motion]",
      selectors: [".design-dna-2026__header > *", ".design-dna-2026__grid > *"],
    },
    {
      root: ".concept-2026[data-pdp-motion]",
      selectors: [".concept-2026__header > *", ".concept-2026__card"],
    },
    {
      root: ".in-action-2026[data-pdp-motion]",
      selectors: [".in-action-2026__header > *", ".bento-2026__item"],
    },
    {
      root: ".tech-talk-2026[data-pdp-motion]",
      selectors: [".tech-talk-2026__header > *", ".tech-talk-2026__video", ".tech-talk-2026__content"],
    },
    {
      root: ".ddsa-section-2026[data-pdp-motion]",
      selectors: [".ddsa-2026__image", ".ddsa-2026__content", ".ddsa-2026__benefit"],
    },
    {
      root: ".reviews-2026[data-pdp-motion]",
      selectors: [".reviews-2026__header > *", ".reviews-2026__stat", ".review-card-2026"],
    },
    {
      root: ".cross-sell-2026[data-pdp-motion]",
      selectors: [".cross-sell-2026__header > *", ".cross-sell-2026__card"],
    },
    {
      root: ".related-2026[data-pdp-motion]",
      selectors: [".related-2026__header > *", ".related-2026__card", ".related-2026__empty"],
    },
  ];

  function pluginsReady() {
    return typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined";
  }

  function reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  // Resolve before splitting the board name so line wrapping uses final font metrics
  // (no reflow after reveal). 900ms fallback mirrors the inline <head> guard so the
  // title is never stuck hidden if fonts never settle.
  function fontsReady(cb) {
    var done = false;
    function go() {
      if (done) return;
      done = true;
      cb();
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(go);
    } else {
      go();
    }
    window.setTimeout(go, 900);
  }

  // Premium heading reveal (the line/word language used across PDP titles). Fonts are
  // already settled when this runs (call inside fontsReady) so the first split is final;
  // autoSplit only re-splits on later resize. Pass opts.delay for an on-load beat or
  // opts.scrollTrigger to tie it to scroll. Any failure reveals the text plainly — never
  // stuck. opts.type: "lines" (masked, for big titles) or "words" (for short headings).
  function runSplit(el, opts) {
    opts = opts || {};
    var type = opts.type === "words" ? "words" : "lines";
    var from = type === "words" ? { y: 24, opacity: 0 } : { yPercent: 110, opacity: 0 };

    if (typeof SplitText === "undefined") {
      gsap.set(el, { opacity: 1, y: 0, clearProps: "transform" });
      return;
    }
    try {
      el._splitInstance = SplitText.create(el, {
        type: type,
        mask: type === "lines" ? "lines" : false,
        aria: "auto",
        autoSplit: true,
        onSplit: function (self) {
          gsap.set(el, { opacity: 1 });
          var parts = self[type];
          if (!parts || !parts.length) return;
          if (el.hasAttribute("data-split-done")) {
            gsap.set(parts, { yPercent: 0, y: 0, opacity: 1, clearProps: "willChange" });
            return;
          }
          gsap.set(parts, from);
          gsap.set(parts, { willChange: "transform" });
          var vars = {
            opacity: 1,
            duration: type === "words" ? 0.6 : 0.9,
            stagger: type === "words" ? 0.05 : 0.08,
            ease: type === "words" ? "power3.out" : "expo.out",
            onComplete: function () {
              el.setAttribute("data-split-done", "");
              gsap.set(parts, { clearProps: "willChange" });
            },
          };
          if (type === "words") vars.y = 0;
          else vars.yPercent = 0;
          if (opts.scrollTrigger) vars.scrollTrigger = opts.scrollTrigger;
          if (opts.delay != null) vars.delay = opts.delay;
          return gsap.to(parts, vars);
        },
      });
    } catch (e) {
      gsap.set(el, { opacity: 1, y: 0, clearProps: "transform" });
    }
  }

  function revertHeroSplits(root) {
    root.querySelectorAll(".product-hero__title, [data-pdp-split]").forEach(function (el) {
      if (el._splitInstance && typeof el._splitInstance.revert === "function") {
        try {
          el._splitInstance.revert();
        } catch (e) {
          /* no-op */
        }
      }
      el._splitInstance = null;
      el.removeAttribute("data-split-done");
    });
  }

  function isBound(el) {
    return el && el.hasAttribute(BOUND);
  }

  function markBound(el) {
    el.setAttribute(BOUND, "");
  }

  function markVisible(el) {
    el.classList.add(VISIBLE);
  }

  function killInRoot(root) {
    if (!pluginsReady()) return;
    ScrollTrigger.getAll().forEach(function (st) {
      var trigger = st.trigger;
      if (trigger && root.contains(trigger)) st.kill();
    });
  }

  function unbindInRoot(root) {
    if (!root) return;
    if (root.hasAttribute && root.hasAttribute(BOUND)) root.removeAttribute(BOUND);
    root.querySelectorAll("[" + BOUND + "]").forEach(function (el) {
      el.removeAttribute(BOUND);
    });
    revertHeroSplits(root);
  }

  function initProductHero(root) {
    root.querySelectorAll(".product-hero").forEach(function (hero) {
      if (isBound(hero)) return;
      markBound(hero);

      var copy = hero.querySelector(".product-hero__copy");
      var media = hero.querySelector(".product-hero__media");
      var image = hero.querySelector(".product-hero__image, .product-hero__video");

      document.documentElement.classList.add("product-motion-ready");

      if (copy) {
        markBound(copy);
        var eyebrow = copy.querySelector(".product-hero__eyebrow");
        var title = copy.querySelector(".product-hero__title");
        var chips = copy.querySelector(".product-hero__chips");
        [eyebrow, title, chips].filter(Boolean).forEach(markBound);
        gsap.set(copy, { opacity: 1 });

        // Eyebrow + chips: immediate y-fade. Title: handcrafted line reveal (the signature),
        // which defers slightly so the board name lands as its own beat.
        var fadeItems = [eyebrow, chips].filter(Boolean);
        if (fadeItems.length) gsap.set(fadeItems, { opacity: 0, y: 28 });

        var tl = gsap.timeline({ defaults: { ease: "power3.out" } });
        if (eyebrow) tl.to(eyebrow, { opacity: 1, y: 0, duration: 0.82 }, 0);
        if (chips) tl.to(chips, { opacity: 1, y: 0, duration: 0.82 }, 0.24);

        if (title) {
          // Hide synchronously so the CSS fallback entrance never flashes before the split.
          gsap.set(title, { opacity: 0 });
          if (typeof SplitText === "undefined") {
            gsap.set(title, { opacity: 0, y: 28 });
            tl.to(title, { opacity: 1, y: 0, duration: 0.82 }, 0.12);
          } else {
            fontsReady(function () {
              runSplit(title, { type: "lines", delay: 0.16 });
            });
          }
        }
      }

      if (image) {
        markBound(image);
        gsap.fromTo(image, { scale: 1.06 }, { scale: 1, duration: 1.5, ease: "power2.out", overwrite: "auto" });
      }

      if (media) {
        markBound(media);
        gsap.fromTo(
          media,
          { yPercent: 0 },
          {
            yPercent: 10,
            ease: "none",
            scrollTrigger: {
              trigger: hero,
              start: "top top",
              end: "bottom top",
              scrub: 0.55,
            },
          },
        );
      }
    });
  }

  function collectTargets(section, selectors) {
    var targets = [];
    selectors.forEach(function (selector) {
      section.querySelectorAll(selector).forEach(function (el) {
        // [data-pdp-split] headings get their own SplitText reveal, not the block fade.
        if (!isBound(el) && !el.hasAttribute("data-pdp-split")) targets.push(el);
      });
    });
    return targets;
  }

  function initPdpSections(root) {
    SECTION_TARGETS.forEach(function (config) {
      root.querySelectorAll(config.root).forEach(function (section) {
        if (isBound(section)) return;

        var targets = collectTargets(section, config.selectors);
        var splitEls = Array.prototype.slice.call(section.querySelectorAll("[data-pdp-split]"));
        if (!targets.length && !splitEls.length) return;

        markBound(section);
        targets.forEach(markBound);

        if (targets.length) {
          gsap.set(targets, { opacity: 0, y: 28 });
          gsap.to(targets, {
            opacity: 1,
            y: 0,
            duration: 0.88,
            stagger: 0.1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: section,
              start: "top 84%",
              once: true,
            },
            onComplete: function () {
              markVisible(section);
              targets.forEach(markVisible);
            },
          });
        } else {
          markVisible(section);
        }

        // Heading word/line reveal (same SplitText language as the PDP hero title),
        // scroll-triggered with the section. Fonts settle first so wrapping is final.
        splitEls.forEach(function (el) {
          fontsReady(function () {
            runSplit(el, {
              type: el.dataset.pdpSplit === "lines" ? "lines" : "words",
              scrollTrigger: { trigger: section, start: "top 84%", once: true },
            });
          });
        });
      });
    });
  }

  function designModeReveal(root) {
    revertHeroSplits(root);
    root
      .querySelectorAll(
        ".product-hero__copy, .product-hero__title, .product-hero__image, [data-pdp-split], [data-pdp-motion]",
      )
      .forEach(function (el) {
        gsap.set(el, { clearProps: "all", opacity: 1, y: 0, scale: 1 });
        markVisible(el);
      });
    root
      .querySelectorAll(
        ".concept-2026__card, .bento-2026__item, .design-dna-2026__grid > *, .tech-talk-2026__video, .tech-talk-2026__content, .ddsa-2026__image, .ddsa-2026__content, .ddsa-2026__benefit, .reviews-2026__stat, .review-card-2026, .cross-sell-2026__card, .related-2026__card, .related-2026__empty",
      )
      .forEach(function (el) {
        gsap.set(el, { clearProps: "all", opacity: 1, y: 0 });
        markVisible(el);
      });
  }

  function boot(root, designMode) {
    if (!pluginsReady()) {
      document.documentElement.classList.add("product-motion-reduced");
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    if (reducedMotion()) {
      document.documentElement.classList.add("product-motion-reduced");
      return;
    }

    if (designMode) {
      designModeReveal(root);
      ScrollTrigger.refresh();
      return;
    }

    initProductHero(root);
    initPdpSections(root);
    ScrollTrigger.refresh();
  }

  function onReady() {
    boot(document, false);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady);
  } else {
    onReady();
  }

  if (typeof Shopify !== "undefined" && Shopify.designMode) {
    document.addEventListener("shopify:section:load", function (event) {
      killInRoot(event.target);
      unbindInRoot(event.target);
      boot(event.target, false);
    });

    document.addEventListener("shopify:section:unload", function (event) {
      killInRoot(event.target);
      unbindInRoot(event.target);
    });

    document.addEventListener("shopify:section:reorder", function () {
      if (pluginsReady()) ScrollTrigger.refresh();
    });

    document.addEventListener("shopify:section:select", function (event) {
      boot(event.target, true);
    });
  }

  document.addEventListener("related-products:updated", function (event) {
    if (!pluginsReady() || reducedMotion()) return;
    var sectionId = event.detail && event.detail.sectionId;
    var root = sectionId ? document.getElementById("shopify-section-" + sectionId) : document;
    if (!root) return;
    killInRoot(root);
    unbindInRoot(root);
    initPdpSections(root);
    ScrollTrigger.refresh();
  });

  window.ExocetProductMotion = {
    refresh: function () {
      if (pluginsReady()) ScrollTrigger.refresh();
    },
    boot: boot,
  };

  if (window.ExocetMotion) {
    ExocetMotion.register(window.ExocetProductMotion);
  }
})();
